import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { F0Dialog, F0Text } from '@factorialco/f0-react'
import { MAX_ESIGNATURE_BULK_CREATE } from 'config/Vars'
import { observer } from 'mobx-react'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import type { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import { useDialogPortalContainer } from 'modules/documents/components/DocumentPreview/useDialogPortalContainer'
import { useCreateEsignatureRequests } from 'modules/esignatures/hooks/requests/useCreateEsignatureRequests'

interface Props {
  document: Document
  onClose: () => void
  useF0?: boolean
}

const EsignatureModal = ({ document, onClose, useF0 = false }: Props) => {
  const employee = document.employee
  const { create } = useCreateEsignatureRequests()
  const { access: currentAccess } = useCurrentSession()
  const portalContainer = useDialogPortalContainer()

  const [selectedIds, setSelectedIds] = React.useState<string[]>(
    employee ? [employee.access.id] : []
  )
  const [submitting, setSubmitting] = React.useState(false)

  const flashMaxError = () => {
    app.setFlash({
      type: 'wrong',
      message: i18n.t('documents.modal.esignature.error_max_selected_users', {
        count: MAX_ESIGNATURE_BULK_CREATE,
      }),
    })
  }

  const onChange = (state: EmployeeSelectorV2MultiSelectState) => {
    if (state.type === 'manual') {
      if (state.ids.length >= MAX_ESIGNATURE_BULK_CREATE) return flashMaxError()
      setSelectedIds(state.ids)
    }
  }

  const handleF0Submit = async () => {
    setSubmitting(true)
    app.setProgress({ message: i18n.t('common.loading') })

    try {
      await create({
        documentId: document.id,
        requesterId: currentAccess.id,
        signeesId: selectedIds,
      })

      app.setProgress(null)
      app.setFlash({ type: 'correct', message: i18n.t('common.saved_successfully') })
      onClose()
    } catch {
      app.setProgress(null)
      app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_error') })
    } finally {
      setSubmitting(false)
    }
  }

  const { running, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      app.setProgress({
        message: i18n.t('common.loading'),
      })

      await create({
        documentId: document.id,
        requesterId: currentAccess.id,
        signeesId: selectedIds,
      })

      app.setProgress(null)

      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      closePortal()
    },
    {
      successFlash: false,
    }
  )

  if (useF0) {
    return (
      <F0Dialog
        isOpen
        width="sm"
        title={i18n.t('documents.modal.esignature.modal.title')}
        onClose={onClose}
        container={portalContainer}
        primaryAction={{
          label: i18n.tp('documents.modal.esignature.modal.submit', {
            count: selectedIds.length,
          }),
          onClick: handleF0Submit,
          loading: submitting,
          disabled: !selectedIds.length,
        }}
        secondaryAction={{
          label: i18n.t('common.cancel'),
          onClick: onClose,
        }}
      >
        <F0Text
          variant="description"
          content={i18n.t('documents.modal.esignature.modal.description')}
        />
        <EmployeeSelectorV2
          multiple
          useAccessId
          value={selectedIds}
          onChange={onChange}
          filters={['workplace', 'team', 'legalEntity', 'accessStatus']}
          options={{ asList: true, disableSelectAll: true }}
        />
      </F0Dialog>
    )
  }

  return (
    <Modal
      illustration="esignature"
      title={i18n.t('documents.modal.esignature.modal.title')}
      description={i18n.t('documents.modal.esignature.modal.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button submit disabled={running || !selectedIds.length}>
          {i18n.tp('documents.modal.esignature.modal.submit', {
            count: selectedIds.length,
          })}
        </Button>
      }
    >
      <EmployeeSelectorV2
        multiple
        useAccessId
        value={selectedIds}
        onChange={onChange}
        filters={['workplace', 'team', 'legalEntity', 'accessStatus']}
        options={{ asList: true, disableSelectAll: true }}
      />
    </Modal>
  )
}

export default observer(EsignatureModal)
